import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import AppDatePicker from "./common/AppDatePicker";
import { getRechartsTooltipProps } from "../theme";

function estimateDailyKwh(series, dateKey) {
  if (!series?.length || !dateKey) return null;
  let sum = 0;
  let n = 0;
  series.forEach((p) => {
    const t = p?.time ? new Date(p.time) : null;
    if (!t || Number.isNaN(t.getTime()) || p?.generation_kw == null) return;
    const key = t.toISOString().slice(0, 10);
    if (key !== dateKey) return;
    sum += p.generation_kw;
    n += 1;
  });
  if (n === 0) return null;
  return (sum / n) * 24;
}

function leadTimeHours(run, targetDate) {
  const targetStart = new Date(`${targetDate}T00:00:00`);
  const created = run?.created_at ? new Date(run.created_at) : null;
  if (!created || Number.isNaN(created.getTime()) || Number.isNaN(targetStart.getTime())) {
    return null;
  }
  return (targetStart.getTime() - created.getTime()) / 3600000;
}

function formatLeadTimeLabel(hours) {
  if (hours == null) return "";
  if (Math.abs(hours) < 30) return `${Math.round(hours)}h before`;
  return `${Math.round(hours / 24)}d before`;
}

function formatRunTimestamp(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
}

export default function ForecastConvergencePanel({
  targetDate,
  onTargetDateChange,
  runs,
  seriesByRun,
  seriesLoadingIds,
  actualRun,
  measured,
  measuredLoading,
  measuredError,
}) {
  const rechartsTooltipProps = getRechartsTooltipProps(useTheme().palette.mode);
  const [open, setOpen] = useState(false);

  const rows = useMemo(() => {
    const measuredValue =
      measured?.available ? estimateDailyKwh(measured.series, targetDate) : null;
    return runs
      .map((run) => {
        const estimate = estimateDailyKwh(seriesByRun[run.run_id], targetDate);
        const leadHours = leadTimeHours(run, targetDate);
        if (estimate == null || leadHours == null) return null;
        const deltaPct =
          measuredValue != null && measuredValue !== 0
            ? ((estimate - measuredValue) / measuredValue) * 100
            : null;
        return {
          run,
          estimate,
          leadHours,
          label: formatLeadTimeLabel(leadHours),
          deltaPct,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.leadHours - a.leadHours);
  }, [runs, seriesByRun, targetDate, measured]);

  const measuredValue = measured?.available ? estimateDailyKwh(measured.series, targetDate) : null;
  const actualWeatherValue = actualRun ? estimateDailyKwh(seriesByRun[actualRun.run_id], targetDate) : null;
  const pendingSeries = runs.some(
    (r) => !seriesByRun[r.run_id] && seriesLoadingIds.includes(r.run_id)
  );

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
      <Accordion
        expanded={open}
        onChange={(_, expanded) => setOpen(expanded)}
        disableGutters
        elevation={0}
        sx={{ "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Forecast Accuracy Over Time
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              See how the forecast for a single day changed across successive runs as that day
              got closer, compared against historical weather and measured output.
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Stack spacing={2}>
            <AppDatePicker
              label="Target date"
              value={targetDate || ""}
              onChange={onTargetDateChange}
              sx={{ maxWidth: 220 }}
            />

            {measuredLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} /> Loading measured data for this date...
              </Box>
            )}
            {measuredError && <Alert severity="error">{measuredError}</Alert>}

            {pendingSeries && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} /> Loading forecast run data for this date...
              </Box>
            )}

            {rows.length === 0 && !pendingSeries ? (
              <Alert severity="info">
                No completed forecast runs cover {targetDate || "the selected date"} yet. Run a
                forecast estimation that includes this date to build up its history.
              </Alert>
            ) : (
              <>
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ left: 24, right: 24, bottom: 24, top: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        label={{
                          value: "Lead time before target day",
                          position: "insideBottom",
                          offset: -16,
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fontWeight: 700 }}
                        width={90}
                        label={{ value: "kWh/day", angle: -90, position: "insideLeft", offset: 14 }}
                      />
                      <RechartsTooltip
                        {...rechartsTooltipProps}
                        formatter={(value) => `${Number(value).toFixed(1)} kWh`}
                        labelFormatter={(label) => label}
                      />
                      <Line
                        type="monotone"
                        dataKey="estimate"
                        name="Forecast estimate"
                        stroke="#1565c0"
                        strokeWidth={2}
                        dot
                      />
                      {measuredValue != null && (
                        <ReferenceLine
                          y={measuredValue}
                          stroke="#212121"
                          strokeDasharray="4 2"
                          label={{ value: "Measured", position: "right", fontSize: 12 }}
                        />
                      )}
                      {actualWeatherValue != null && (
                        <ReferenceLine
                          y={actualWeatherValue}
                          stroke="#2e7d32"
                          strokeDasharray="4 2"
                          label={{ value: "Historical weather", position: "left", fontSize: 12 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Run created</TableCell>
                      <TableCell>Lead time</TableCell>
                      <TableCell align="right">Estimate (kWh/day)</TableCell>
                      <TableCell align="right">Δ vs measured</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.run.run_id}>
                        <TableCell>{formatRunTimestamp(row.run.created_at)}</TableCell>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{row.estimate.toFixed(1)}</TableCell>
                        <TableCell align="right">
                          {row.deltaPct != null ? `${row.deltaPct >= 0 ? "+" : ""}${row.deltaPct.toFixed(1)}%` : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
